import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { 
  useCreditDebitTrackers, 
  useDeleteCreditDebitTracker, 
  useUpdateCreditDebitTrackerStatus 
} from '../hooks/useCreditDebitTracker';
import { CreditDebitTracker, AccountType, CreditDebitTrackerFilterParams } from '../types/credit-debit-tracker.types';

interface CreditDebitTrackerListProps {
  onAdd: () => void;
  onEdit: (creditDebitTracker: CreditDebitTracker) => void;
  onView?: (creditDebitTracker: CreditDebitTracker) => void;
}

const CreditDebitTrackerList: React.FC<CreditDebitTrackerListProps> = ({
  onAdd,
  onEdit,
  onView
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CreditDebitTrackerFilterParams>({});
  const [showFilters, setShowFilters] = useState(false);

  const queryParams: CreditDebitTrackerFilterParams = {
    skip: page * rowsPerPage,
    take: rowsPerPage,
    search: searchTerm || undefined,
    ...filters
  };

  const { creditDebitTrackers, isLoading, error, refetch } = useCreditDebitTrackers(queryParams);
  const deleteMutation = useDeleteCreditDebitTracker();
  const updateStatusMutation = useUpdateCreditDebitTrackerStatus();

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((field: keyof CreditDebitTrackerFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined
    }));
    setPage(0);
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete transaction:', error);
      }
    }
  }, [deleteMutation]);

  const handleToggleStatus = useCallback(async (id: string, currentStatus: boolean) => {
    try {
      await updateStatusMutation.mutateAsync({ id, isActive: !currentStatus });
    } catch (error) {
      console.error('Failed to update transaction status:', error);
    }
  }, [updateStatusMutation]);

  const getTypeColor = (type: AccountType) => {
    return type === AccountType.CREDIT ? 'success' : 'error';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load transactions. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          Credit/Debit Tracker
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
        >
          Add Transaction
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Grid>
        </Grid>

        {/* Advanced Filters */}
        {showFilters && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type || ''}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value={AccountType.CREDIT}>Credit</MenuItem>
                  <MenuItem value={AccountType.DEBIT}>Debit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({});
                  setSearchTerm('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : creditDebitTrackers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              creditDebitTrackers.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>
                    {format(new Date(transaction.date), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.type}
                      color={getTypeColor(transaction.type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.chartOfAccount?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transaction.chartOfAccount?.accountCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={transaction.description}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {transaction.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={transaction.type === AccountType.CREDIT ? 'success.main' : 'error.main'}
                    >
                      {formatCurrency(transaction.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {transaction.location ? (
                      <Box>
                        <Typography variant="body2">
                          {transaction.location.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {transaction.location.code}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {transaction.referenceNumber ? (
                      <Box>
                        <Typography variant="body2">
                          {transaction.referenceNumber}
                        </Typography>
                        {transaction.referenceDate && (
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(transaction.referenceDate), 'dd/MM/yyyy')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={transaction.isActive ? 'Active' : 'Inactive'}
                      color={transaction.isActive ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleStatus(transaction.id, transaction.isActive)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {onView && (
                        <IconButton
                          size="small"
                          onClick={() => onView(transaction)}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => onEdit(transaction)}
                        title="Edit Transaction"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(transaction.id)}
                        title="Delete Transaction"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={-1} // We don't have total count from API
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default CreditDebitTrackerList;
